import { ed25519 } from "@noble/curves/ed25519";
import { parseW10ControlJsonBytes } from "@klass-share/protocol";

import { WEB_DEPLOYMENT_CONFIG } from "./deployment-config.js";
import embeddedTransportTrust from "./w8-transport-trust.json";

const JOIN_GRANT_KEYS = Object.freeze([
  "typ",
  "schemaVersion",
  "protocolVersion",
  "licenseId",
  "hostId",
  "sessionId",
  "sessionBindingSha256",
  "grantNonce",
  "tier",
  "issuedAt",
  "expiresAt",
  "keyId",
]);
const KEY_ID_PATTERN = /^[A-Za-z0-9_-]{16}$/u;
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;
const HOST_ID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{8}$/u;
const GRANT_TTL_SECONDS = 60;
const GRANT_WALL_TOLERANCE_SECONDS = 300;
const GRANT_MONOTONIC_USE_WINDOW_MS = 60_000;
const BASE64URL_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/u;

export const decodeCanonicalBase64Url = (
  value: unknown,
  expectedBytes: number | undefined,
  label: string,
): Uint8Array => {
  if (typeof value !== "string" || !BASE64URL_PATTERN.test(value)) {
    throw new TypeError(`${label} is not base64url`);
  }
  const remainder = value.length % 4;
  const finalIndex = BASE64URL_ALPHABET.indexOf(value[value.length - 1] ?? "");
  if (
    finalIndex < 0 ||
    remainder === 1 ||
    (remainder === 2 && (finalIndex & 0x0f) !== 0) ||
    (remainder === 3 && (finalIndex & 0x03) !== 0)
  ) {
    throw new TypeError(`${label} is not canonical base64url`);
  }

  const output: number[] = [];
  let accumulator = 0;
  let bitCount = 0;
  for (const character of value) {
    const digit = BASE64URL_ALPHABET.indexOf(character);
    if (digit < 0) throw new TypeError(`${label} is not base64url`);
    accumulator = (accumulator << 6) | digit;
    bitCount += 6;
    if (bitCount >= 8) {
      bitCount -= 8;
      output.push((accumulator >>> bitCount) & 0xff);
      accumulator &= (1 << bitCount) - 1;
    }
  }
  if (accumulator !== 0) {
    throw new TypeError(`${label} is not canonical base64url`);
  }
  const bytes = Uint8Array.from(output);
  if (expectedBytes !== undefined && bytes.byteLength !== expectedBytes) {
    throw new TypeError(`${label} has the wrong byte length`);
  }
  return bytes;
};

export type W8JoinGrantVerificationCode =
  | "GRANT_EXPIRED"
  | "GRANT_INVALID"
  | "UNSUPPORTED_GRANT_SCHEMA";

export class W8JoinGrantVerificationError extends Error {
  public readonly code: W8JoinGrantVerificationCode;

  public constructor(code: W8JoinGrantVerificationCode) {
    super(code);
    this.name = "W8JoinGrantVerificationError";
    this.code = code;
  }
}

export interface W8TransportVerificationKey {
  readonly keyId: string;
  readonly publicKeyBase64Url: string;
}

export interface W8JoinGrantEnvelope {
  readonly hdr: Readonly<{ keyId: string }>;
  readonly payload: string;
  readonly sig: string;
}

export interface W8JoinGrantPayloadV1 {
  readonly typ: "klass-join-grant";
  readonly schemaVersion: 1;
  readonly protocolVersion: number;
  readonly licenseId: string;
  readonly hostId: string;
  readonly sessionId: string;
  readonly sessionBindingSha256: string;
  readonly grantNonce: string;
  readonly tier: Readonly<{ maxClients: 5 | 10 }>;
  readonly issuedAt: number;
  readonly expiresAt: number;
  readonly keyId: string;
}

export interface W8JoinGrantRouteBinding {
  readonly licenseId: string;
  readonly hostId: string;
  readonly sessionId: string;
  readonly protocolVersion: number;
  readonly grantNonce: string;
}

export interface W8JoinGrantVerifierClock {
  readonly wallClockSeconds: number;
  readonly monotonicMilliseconds: number;
}

export interface W8PrevalidatedJoinGrant {
  readonly envelope: W8JoinGrantEnvelope;
  readonly payload: W8JoinGrantPayloadV1;
  readonly receivedMonotonicMilliseconds: number;
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const hasOrderedKeys = (
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean => {
  const actual = Object.keys(value);
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
};

const isNonnegativeSafeInteger = (value: unknown): value is number =>
  Number.isSafeInteger(value) && (value as number) >= 0;
const isNonnegativeFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const parseEmbeddedTransportTrust =
  (): readonly W8TransportVerificationKey[] => {
    if (
      embeddedTransportTrust.schemaVersion !== 1 ||
      !Array.isArray(embeddedTransportTrust.keys) ||
      embeddedTransportTrust.keys.length < 1 ||
      embeddedTransportTrust.keys.length > 2
    ) {
      throw new TypeError("embedded W8 transport trust is invalid");
    }
    const seen = new Set<string>();
    const keys = embeddedTransportTrust.keys.map((entry) => {
      if (
        !hasOrderedKeys(entry, ["keyId", "publicKeyBase64Url"]) ||
        !KEY_ID_PATTERN.test(entry.keyId) ||
        seen.has(entry.keyId)
      ) {
        throw new TypeError("embedded W8 transport key is invalid");
      }
      decodeCanonicalBase64Url(
        entry.publicKeyBase64Url,
        32,
        "embedded W8 transport public key",
      );
      seen.add(entry.keyId);
      return Object.freeze({
        keyId: entry.keyId,
        publicKeyBase64Url: entry.publicKeyBase64Url,
      });
    });
    return Object.freeze(keys);
  };

const PROD_TRANSPORT_KEYS = parseEmbeddedTransportTrust();

/** DEV remains fail-closed until a checked-in DEV delegation manifest exists. */
export const W8_EMBEDDED_TRANSPORT_KEYS: readonly W8TransportVerificationKey[] =
  WEB_DEPLOYMENT_CONFIG.environment === "prod"
    ? PROD_TRANSPORT_KEYS
    : Object.freeze([]);

const invalid = (): never => {
  throw new W8JoinGrantVerificationError("GRANT_INVALID");
};

const decodeEnvelope = (
  value: unknown,
  trustedKeys: readonly W8TransportVerificationKey[],
): {
  readonly envelope: W8JoinGrantEnvelope;
} => {
  if (
    !isPlainRecord(value) ||
    !hasOrderedKeys(value, ["hdr", "payload", "sig"])
  )
    return invalid();
  if (!isPlainRecord(value.hdr)) return invalid();
  const header = value.hdr;
  const keyId = header.keyId;
  if (
    !hasOrderedKeys(header, ["keyId"]) ||
    typeof keyId !== "string" ||
    !KEY_ID_PATTERN.test(keyId) ||
    typeof value.payload !== "string" ||
    typeof value.sig !== "string"
  ) {
    return invalid();
  }
  const selected = trustedKeys.find((key) => key.keyId === keyId);
  if (selected === undefined) return invalid();
  const signature = decodeCanonicalBase64Url(
    value.sig,
    64,
    "W8 join-grant signature",
  );
  const publicKey = decodeCanonicalBase64Url(
    selected.publicKeyBase64Url,
    32,
    "W8 transport public key",
  );
  const signingInput = new TextEncoder().encode(value.payload);
  let verified = false;
  try {
    verified = ed25519.verify(signature, signingInput, publicKey, {
      zip215: false,
    });
  } catch {
    verified = false;
  }
  if (!verified) return invalid();
  return {
    envelope: Object.freeze({
      hdr: Object.freeze({ keyId }),
      payload: value.payload,
      sig: value.sig,
    }),
  };
};

const decodeSignedPayload = (
  payloadSegment: string,
  headerKeyId: string,
): W8JoinGrantPayloadV1 => {
  let bytes: Uint8Array;
  let parsed: unknown;
  try {
    bytes = decodeCanonicalBase64Url(
      payloadSegment,
      undefined,
      "W8 join-grant payload",
    );
    parsed = parseW10ControlJsonBytes(bytes);
  } catch {
    return invalid();
  }
  if (!isPlainRecord(parsed)) return invalid();
  const keys = Object.keys(parsed);
  if (
    keys[0] !== "typ" ||
    keys[1] !== "schemaVersion" ||
    parsed.typ !== "klass-join-grant" ||
    !Number.isSafeInteger(parsed.schemaVersion) ||
    (parsed.schemaVersion as number) <= 0
  ) {
    return invalid();
  }
  const compact = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  if (JSON.stringify(parsed) !== compact) return invalid();
  if (parsed.schemaVersion !== 1) {
    throw new W8JoinGrantVerificationError("UNSUPPORTED_GRANT_SCHEMA");
  }
  if (!hasOrderedKeys(parsed, JOIN_GRANT_KEYS)) return invalid();
  if (
    !Number.isSafeInteger(parsed.protocolVersion) ||
    (parsed.protocolVersion as number) <= 0 ||
    typeof parsed.licenseId !== "string" ||
    !UUID_V4_PATTERN.test(parsed.licenseId) ||
    typeof parsed.hostId !== "string" ||
    !HOST_ID_PATTERN.test(parsed.hostId) ||
    typeof parsed.sessionId !== "string" ||
    !UUID_V4_PATTERN.test(parsed.sessionId) ||
    typeof parsed.sessionBindingSha256 !== "string" ||
    typeof parsed.grantNonce !== "string" ||
    !isPlainRecord(parsed.tier) ||
    !hasOrderedKeys(parsed.tier, ["maxClients"]) ||
    (parsed.tier.maxClients !== 5 && parsed.tier.maxClients !== 10) ||
    !isNonnegativeSafeInteger(parsed.issuedAt) ||
    !isNonnegativeSafeInteger(parsed.expiresAt) ||
    parsed.expiresAt !== parsed.issuedAt + GRANT_TTL_SECONDS ||
    typeof parsed.keyId !== "string" ||
    parsed.keyId !== headerKeyId
  ) {
    return invalid();
  }
  try {
    decodeCanonicalBase64Url(
      parsed.sessionBindingSha256,
      32,
      "W8 session binding digest",
    );
    decodeCanonicalBase64Url(parsed.grantNonce, 16, "W8 grant nonce");
  } catch {
    return invalid();
  }
  return Object.freeze({
    typ: "klass-join-grant",
    schemaVersion: 1,
    protocolVersion: parsed.protocolVersion as number,
    licenseId: parsed.licenseId,
    hostId: parsed.hostId,
    sessionId: parsed.sessionId,
    sessionBindingSha256: parsed.sessionBindingSha256,
    grantNonce: parsed.grantNonce,
    tier: Object.freeze({ maxClients: parsed.tier.maxClients }),
    issuedAt: parsed.issuedAt,
    expiresAt: parsed.expiresAt,
    keyId: parsed.keyId,
  });
};

const assertRouteBinding = (
  payload: W8JoinGrantPayloadV1,
  expected: W8JoinGrantRouteBinding,
): void => {
  if (
    payload.licenseId !== expected.licenseId ||
    payload.hostId !== expected.hostId ||
    payload.sessionId !== expected.sessionId ||
    payload.protocolVersion !== expected.protocolVersion ||
    payload.grantNonce !== expected.grantNonce
  ) {
    invalid();
  }
};

const assertWallClock = (
  payload: W8JoinGrantPayloadV1,
  wallClockSeconds: number,
): void => {
  if (
    !isNonnegativeSafeInteger(wallClockSeconds) ||
    wallClockSeconds < payload.issuedAt - GRANT_WALL_TOLERANCE_SECONDS ||
    wallClockSeconds > payload.expiresAt + GRANT_WALL_TOLERANCE_SECONDS
  ) {
    throw new W8JoinGrantVerificationError("GRANT_EXPIRED");
  }
};

export function verifyW8JoinGrantEnvelope(
  envelope: unknown,
  expected: W8JoinGrantRouteBinding,
  clock: W8JoinGrantVerifierClock,
  trustedKeys: readonly W8TransportVerificationKey[] = W8_EMBEDDED_TRANSPORT_KEYS,
): W8PrevalidatedJoinGrant {
  try {
    if (
      !isNonnegativeFiniteNumber(clock.monotonicMilliseconds) ||
      trustedKeys.length < 1 ||
      trustedKeys.length > 2
    ) {
      return invalid();
    }
    const decoded = decodeEnvelope(envelope, trustedKeys);
    const payload = decodeSignedPayload(
      decoded.envelope.payload,
      decoded.envelope.hdr.keyId,
    );
    assertRouteBinding(payload, expected);
    assertWallClock(payload, clock.wallClockSeconds);
    return Object.freeze({
      envelope: decoded.envelope,
      payload,
      receivedMonotonicMilliseconds: clock.monotonicMilliseconds,
    });
  } catch (error) {
    if (error instanceof W8JoinGrantVerificationError) throw error;
    return invalid();
  }
}

export function assertW8JoinGrantUseWindow(
  grant: W8PrevalidatedJoinGrant,
  clock: W8JoinGrantVerifierClock,
): void {
  assertWallClock(grant.payload, clock.wallClockSeconds);
  if (
    !isNonnegativeFiniteNumber(clock.monotonicMilliseconds) ||
    clock.monotonicMilliseconds < grant.receivedMonotonicMilliseconds ||
    clock.monotonicMilliseconds - grant.receivedMonotonicMilliseconds >
      GRANT_MONOTONIC_USE_WINDOW_MS
  ) {
    throw new W8JoinGrantVerificationError("GRANT_EXPIRED");
  }
}
